import { Config } from '../context/Config';
import { formatNumber } from './numberUtils';

const CURRENCY_DEFAULT = 'GBP';

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: 'US$',
  EUR: '€',
  GBP: '£',
  AUD: 'AU$',
  CAD: 'CA$',
  NZD: 'NZ$',
  CHF: 'CHF',
  JPY: '¥',
  CNY: 'CN¥',
  HKD: 'HK$',
  SGD: 'S$',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei',
  BGN: 'лв',
  TRY: '₺',
  ILS: '₪',
  AED: 'AED',
  SAR: 'SAR',
  KWD: 'KWD',
  INR: '₹',
  IDR: 'Rp',
  MYR: 'RM',
  THB: '฿',
  VND: '₫',
  PHP: '₱',
  MXN: 'MX$',
  ARS: 'AR$',
  BRL: 'R$',
  CLP: 'CLP$',
  COP: 'COL$',
  PEN: 'S/',
  ZAR: 'R',
  NGN: '₦',
  EGP: 'E£',
  MAD: 'MAD',
  XAF: 'FCFA',
  XOF: 'CFA',
  XPF: 'CFP',
  BBD: 'Bds$',
  BMD: 'BD$',
  KYD: 'CI$',
  TTD: 'TT$',
  PAB: 'B/.',
  XCD: 'EC$',
  HTG: 'G',
  AWG: 'Afl.',
  ANG: 'NAƒ',
  SRD: 'SR$',
  JMD: 'J$',
  GYD: 'GY$',
  FJD: 'FJ$',
  PGK: 'K',
  TOP: 'T$',
  WST: 'WS$',
  VUV: 'VT',
  XCG: '¤',
};

export const formatPriceValue = (
  locale: string,
  price: number,
  options?: Intl.NumberFormatOptions,
) => {
  const formattedPrice = formatNumber(locale, price, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
    ...options,
  }).replace(/[\u202F\u00A0\s]/g, ''); //fr uses \u202F as 'thousand's separator

  return formattedPrice;
};

export const formatCurrency = (
  locale: string,
  currency: string,
  price: string | number,
  options?: Intl.NumberFormatOptions,
) => {
  const value = Number(price);

  if (
    isNaN(value) ||
    (typeof price === 'string' && price.trim().length === 0)
  ) {
    throw Error(`Can't format value "${price}" as currency`);
  }

  const symbol = CURRENCY_SYMBOL[currency];
  const formattedPrice = formatPriceValue(locale, value, options);

  return `${symbol}${formattedPrice}`;
};

export const getCurrencyCode = (locale: string, currency: string) => {
  return CURRENCY_SYMBOL[currency];
};

export const getCurrencyName = (locale: string, currency: string) => {
  return new Intl.DisplayNames(locale, {
    type: 'currency',
  }).of(currency);
};

export const getCurrencyLabel = (locale: string, currency: string) =>
  `(${getCurrencyCode(locale, currency)}) ${currency}`;

export const getCurrencyOrDefault = (
  queryParamValue: string | undefined,
  cookieValue: string | undefined,
  config: Config,
) => {
  const { PAYMENT_DEFAULT_CURRENCY, PAYMENT_SUPPORTED_CURRENCIES } = config;

  const defaultCurrency =
    PAYMENT_DEFAULT_CURRENCY ??
    PAYMENT_SUPPORTED_CURRENCIES?.at(0) ??
    CURRENCY_DEFAULT;

  const value = queryParamValue || cookieValue;

  const currency =
    value && PAYMENT_SUPPORTED_CURRENCIES?.includes(value)
      ? value
      : defaultCurrency;

  return currency;
};
